// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Competition from "./competition.js"
import Highscore from "./highscore.js"

const modelClassData = {
  "attributes": {
    "competition_id": {
      "column": {
        "default": null,
        "name": "competition_id",
        "null": false,
        "type": "uuid"
      },
      "name": "competition_id",
      "selected_by_default": null,
      "translated": null
    },
    "highscore_id": {
      "column": {
        "default": null,
        "name": "highscore_id",
        "null": false,
        "type": "uuid"
      },
      "name": "highscore_id",
      "selected_by_default": null,
      "translated": null
    },
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    }
  },
  "className": "HighscoreCompetition",
  "collectionKey": "highscore_competitions",
  "collectionName": "highscore_competitions",
  "i18nKey": "highscore_competition",
  "camelizedLower": "highscoreCompetition",
  "name": "HighscoreCompetition",
  "nameDasherized": "highscore-competition",
  "pluralName": "highscore_competitions",
  "ransackable_associations": [
    {
      "className": "Competition",
      "collectionName": "competitions",
      "foreignKey": "competition_id",
      "name": "competition",
      "macro": "belongs_to",
      "resource_name": "Competition",
      "through": null
    },
    {
      "className": "Highscore",
      "collectionName": "highscores",
      "foreignKey": "highscore_id",
      "name": "highscore",
      "macro": "belongs_to",
      "resource_name": "Highscore",
      "through": null
    },
    {
      "className": "Log",
      "collectionName": "logs",
      "foreignKey": "trackable_id",
      "name": "logs",
      "macro": "has_many",
      "resource_name": "Log",
      "through": null
    },
    {
      "className": "Tagging",
      "collectionName": "taggings",
      "foreignKey": "resource_id",
      "name": "taggings",
      "macro": "has_many",
      "resource_name": "Tagging",
      "through": null
    },
    {
      "className": "Tag",
      "collectionName": "tags",
      "foreignKey": "tag_id",
      "name": "tags",
      "macro": "has_many",
      "resource_name": "Tag",
      "through": "taggings"
    },
    {
      "className": "Tenant",
      "collectionName": "tenants",
      "foreignKey": "tenant_id",
      "name": "tenant",
      "macro": "belongs_to",
      "resource_name": "Tenant",
      "through": null
    },
    {
      "className": "WorkerPlugins::WorkplaceLink",
      "collectionName": "workplace_links",
      "foreignKey": "resource_id",
      "name": "workplace_links",
      "macro": "has_many",
      "resource_name": "WorkplaceLink",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "competition_id",
      "column": {
        "default": null,
        "name": "competition_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "highscore_id",
      "column": {
        "default": null,
        "name": "highscore_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "id_string",
      "column": null
    },
    {
      "name": "old_competition_id",
      "column": {
        "default": null,
        "name": "old_competition_id",
        "null": true,
        "type": "integer"
      }
    },
    {
      "name": "old_highscore_id",
      "column": {
        "default": null,
        "name": "old_highscore_id",
        "null": true,
        "type": "integer"
      }
    },
    {
      "name": "old_id",
      "column": {
        "default": null,
        "name": "old_id",
        "null": true,
        "type": "integer"
      }
    },
    {
      "name": "tenant_id",
      "column": {
        "default": null,
        "name": "tenant_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [
    {
      "name": "in_current_workplace"
    },
    {
      "name": "soft_deleted_filter"
    },
    {
      "name": "sort_by_in_current_workplace_asc"
    },
    {
      "name": "sort_by_in_current_workplace_desc"
    }
  ],
  "relationships": [
    {
      "className": "Competition",
      "collectionName": "competitions",
      "foreignKey": "competition_id",
      "name": "competition",
      "macro": "belongs_to",
      "resource_name": "Competition",
      "through": null
    },
    {
      "className": "Highscore",
      "collectionName": "highscores",
      "foreignKey": "highscore_id",
      "name": "highscore",
      "macro": "belongs_to",
      "resource_name": "Highscore",
      "through": null
    }
  ],
  "paramKey": "highscore_competition",
  "primaryKey": "id"
}

/** Frontend model for HighscoreCompetition. */
export default class HighscoreCompetition extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {string} */
  competitionId() {
    return this.readAttributeUnderscore("competition_id")
  }

  /** @returns {boolean} */
  hasCompetitionId() {
    const value = this.competitionId()

    return this._isPresent(value)
  }

  /** @returns {string} */
  highscoreId() {
    return this.readAttributeUnderscore("highscore_id")
  }

  /** @returns {boolean} */
  hasHighscoreId() {
    const value = this.highscoreId()

    return this._isPresent(value)
  }

  /** @returns {string} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {import("./competition.js").default | null} */
  competition() {
    return this._readBelongsToReflection({modelClass: Competition, reflectionName: "competition"})
  }

  /** @returns {Promise<import("./competition.js").default | null>} */
  loadCompetition() {
    if (!("competitionId" in this)) throw new Error("Foreign key method wasn't defined: competitionId")

    const id = this.competitionId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "competition",
        model: this,
        modelClass: Competition
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./competition.js").default | null>} */
  ensureCompetitionLoaded() {
    return this.ensureAssociationLoaded("competition")
  }

  /** @returns {import("./highscore.js").default | null} */
  highscore() {
    return this._readBelongsToReflection({modelClass: Highscore, reflectionName: "highscore"})
  }

  /** @returns {Promise<import("./highscore.js").default | null>} */
  loadHighscore() {
    if (!("highscoreId" in this)) throw new Error("Foreign key method wasn't defined: highscoreId")

    const id = this.highscoreId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "highscore",
        model: this,
        modelClass: Highscore
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./highscore.js").default | null>} */
  ensureHighscoreLoaded() {
    return this.ensureAssociationLoaded("highscore")
  }
}
